import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useAppContext } from '../contexts/Storage';
import { generateCreditCheckout, redirectToPaymentLink, resolvePaymentGateway, paymentProviderLabel, fetchPaymentConfig, type PaymentConfig } from '../utils/payments';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

const CreditPurchasePage: React.FC = () => {
  const navigate = useNavigate();
  const { setNavLinks } = useNavLinksContext();
  const { token, user } = useAppContext();

  const [amount, setAmount] = useState<number>(10);
  const [creditPoints, setCreditPoints] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch payment config
  const { data: paymentConfig, isLoading: isLoadingConfig } = useQuery<PaymentConfig, Error>({
    queryKey: ['payment-config'],
    queryFn: async () => {
      if (!token) throw new Error('User not authenticated');
      return fetchPaymentConfig(token);
    },
    enabled: !!token,
  });

  // Helper to get credit point price as number
  const getCreditPointPrice = (): number => {
    if (!paymentConfig) return 10; // Default fallback
    const price = paymentConfig.credit_point_price;
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    // Ensure we don't have division by zero or negative prices
    return numPrice > 0 ? numPrice : 10; // Default to $10 if invalid
  };

  // Calculate credit points based on amount and credit_point_price
  useEffect(() => {
    if (paymentConfig && amount > 0) {
      const creditPointPrice = getCreditPointPrice();
      // TEMPORARY: Currently credit_point_price is in dollars (e.g., 10 means $10 per credit point)
      // TODO: Update when backend returns decimal (e.g., 0.01 for $0.01 per credit point)
      // For now: points = amount / credit_point_price
      const calculatedPoints = Math.round(amount / creditPointPrice);
      setCreditPoints(calculatedPoints);
    }
  }, [amount, paymentConfig]);



  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/credit-points', label: 'Credit Points' },
        { to: '/credit/purchase', label: 'Purchase Points' },
      ],
    });
  }, [setNavLinks]);



  const handleCustomAmountChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    setAmount(numValue);
    // Points calculation is handled in useEffect
  };

  const handlePurchase = async () => {
    if (!token || !user?.id) {
      toast.error('Please login to purchase credit points');
      return;
    }

    if (amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsProcessing(true);

    try {
      const gateway = resolvePaymentGateway(paymentConfig!);
      const paymentResponse = await generateCreditCheckout(token, {
        user_id: user.id,
        amount: amount,
        credit_points: creditPoints,
        redirect_url: `${window.location.origin}/credit-points?purchase_amount=${amount}&purchase_points=${creditPoints}`,
      }, gateway);

      if (paymentResponse.status === 'success' && paymentResponse.payment_link) {
        redirectToPaymentLink(paymentResponse.payment_link);
      } else {
        throw new Error(paymentResponse.message || 'Failed to generate payment link');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to process payment');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="mx-2 p-6">
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-grey_dark mx-auto"></div>
            <p className="mt-4 text-grey_dark">Loading payment configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentConfig) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="mx-2 p-6">
          <div className="text-center py-16 text-grey_dark">
            Failed to load payment configuration. Please try again.
          </div>
        </div>
      </div>
    );
  }

  const paymentGateway = resolvePaymentGateway(paymentConfig);
  const paymentLabel = paymentProviderLabel(paymentGateway);

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar bg-bg_grey_light text-dark overflow-y-scroll">
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--secondary);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--secondary);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        input[type="range"]::-webkit-slider-thumb:hover {
          background: var(--secondary);
          filter: brightness(0.9);
        }
        
        input[type="range"]::-moz-range-thumb:hover {
          background: var(--secondary);
          filter: brightness(0.9);
        }
      `}</style>
      <div className="mx-2">
        {/* Header Section */}
        <div className="p-6 pb-3 bg-light rounded-t-lg mb-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
            <div>
              <h3 className="text-2xl font-medium">Purchase Credit Points</h3>
              <p className="text-grey_dark mt-1">Buy credit points to use for premium features and services</p>
              <p className="text-sm text-primary mt-1">
                Current rate: ${getCreditPointPrice().toFixed(2)} per credit point
              </p>
            </div>

            <Button
              variant="bordered-primary"
              onClick={() => navigate('/credit-points')}
              className="mt-4 md:mt-0"
            >
              Back to Credit Points
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Amount Selection */}
          <div className="lg:col-span-2">
            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-6">Select Amount</h4>
              <div className="space-y-6">
                {/* Slider for amount selection */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-grey_dark">
                      Select Amount (USD)
                    </label>
                    <span className="text-lg font-bold text-primary">${amount.toFixed(2)}</span>
                  </div>
                  
                  <div className="relative pt-1">
                    <input
                      type="range"
                      min="1"
                      max="5000"
                      step="1"
                      value={amount}
                      onChange={(e) => setAmount(parseFloat(e.target.value))}
                      className="w-full h-2 bg-bg_grey_light rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, var(--secondary) 0%, var(--secondary) ${(amount / 5000) * 100}%, #e5e7eb ${(amount / 5000) * 100}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-grey_dark mt-2">
                      <span>$1</span>
                      <span>$1000</span>
                      <span>$2500</span>
                      <span>$5000</span>
                    </div>
                  </div>

                  {/* Quick amount buttons */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {[10, 50, 100, 500, 1000, 2500].map((quickAmount) => (
                      <button
                        key={quickAmount}
                        type="button"
                        onClick={() => setAmount(quickAmount)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          amount === quickAmount
                            ? 'bg-secondary text-white'
                            : 'bg-bg_grey_light text-dark hover:bg-grey_light'
                        }`}
                      >
                        ${quickAmount}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Manual input as backup */}
                <div>
                  <label className="block text-sm font-medium text-grey_dark mb-2">
                    Or enter custom amount
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-grey_dark">$</span>
                    <Input
                      type="number"
                      attributes={{
                        placeholder: 'Enter amount',
                        value: amount.toString(),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => 
                          handleCustomAmountChange(e.target.value),
                        min: '1',
                        max: '10000',
                        step: '0.01',
                      }}
                      className="flex-1"
                    />
                    <span className="text-grey_dark">USD</span>
                  </div>
                </div>

                {/* Credit points display */}
                <div>
                  <label className="block text-sm font-medium text-grey_dark mb-2">
                    Credit Points You'll Receive
                  </label>
                  <div className="bg-bg_grey_light rounded-lg p-6 border border-grey_light">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                      <div className="text-center md:text-left mb-4 md:mb-0">
                        <div className="text-4xl font-bold text-primary">
                          {creditPoints.toLocaleString()}
                        </div>
                        <div className="text-grey_dark mt-1">credit points</div>
                      </div>
                      <div className="text-center md:text-right">
                        <div className="text-sm text-grey_dark mb-1">Exchange Rate</div>
                        <div className="text-lg font-medium text-dark">
                          ${getCreditPointPrice().toFixed(2)} = 1 point
                        </div>
                        <div className="text-xs text-grey_dark mt-1">
                          (${(1 / getCreditPointPrice()).toFixed(4)} points per $1)
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress bar showing value */}
                    {amount > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between text-sm text-grey_dark mb-1">
                          <span>Value</span>
                          <span>${amount.toFixed(2)} USD</span>
                        </div>
                        <div className="w-full bg-bg_grey_light rounded-full h-2">
                          <div 
                            className="bg-secondary rounded-full h-2 transition-all duration-300"
                            style={{ width: `${Math.min((amount / 500) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-light rounded-lg p-6 sticky top-6">
              <h4 className="text-lg font-medium mb-6">Order Summary</h4>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-grey_dark">Amount</span>
                  <span className="font-medium">${amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-grey_dark">Credit Points</span>
                  <span className="font-medium">{creditPoints.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-grey_dark">Processing Fee</span>
                  <span className="font-medium">$0.00</span>
                </div>
                <div className="border-t border-grey_light pt-4">
                  <div className="flex justify-between">
                    <span className="font-medium text-dark">Total</span>
                    <span className="text-xl font-bold text-primary">${amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <Button
                variant="filled-primary"
                onClick={handlePurchase}
                disabled={isProcessing || amount <= 0}
                className="w-full py-3 text-lg"
              >
                {isProcessing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
              </Button>

              <div className="mt-6 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-grey_dark">Secure payment via {paymentLabel}</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-grey_dark">Instant credit points delivery</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-grey_dark">Money-back guarantee</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-light rounded-lg p-6 mt-6">
          <h4 className="text-lg font-medium mb-6">Frequently Asked Questions</h4>
          <div className="space-y-4">
            <div className="border-b border-grey_light pb-4">
              <h5 className="font-medium mb-2">How are credit points calculated?</h5>
              <p className="text-sm text-grey_dark">
                Base rate is $1 = 100 credit points. Packages include bonus points as shown. Custom amounts use the base rate.
              </p>
            </div>
            <div className="border-b border-grey_light pb-4">
              <h5 className="font-medium mb-2">When will I receive my credit points?</h5>
              <p className="text-sm text-grey_dark">
                Credit points are added to your account immediately after successful payment.
              </p>
            </div>
            <div className="border-b border-grey_light pb-4">
              <h5 className="font-medium mb-2">What payment methods are accepted?</h5>
              <p className="text-sm text-grey_dark">
                We accept card payments via {paymentLabel}.
              </p>
            </div>
            <div>
              <h5 className="font-medium mb-2">Can I get a refund?</h5>
              <p className="text-sm text-grey_dark">
                Yes, unused credit points can be refunded within 1 hour of purchase. Contact support for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchasePage;